import { useParams } from 'react-router-dom';
import { Col, Statistic, Typography } from 'antd';
import groupBy from 'lodash/groupBy';
import { useGetSummaryMetricsQuery } from '@/redux';
import { ProjectDto } from '@/core';
import { getMonthOfLastGeneratedReport } from './helpers/getLastGeneratedReport';
import { defaultChartColors } from './helpers/defaultChartColors';
import { displayCurrency } from '@/utils/displayCurrency';
import classes from './ReportsStatistic.module.scss';

type ReportsStatisticPropsType = {
  startDate?: string;
  endDate?: string;
  projectData: ProjectDto;
  costTypesLegends: string[];
};

type CostTypesListProps = {
  costTypes?: { name: string; totalCost: number; color?: string }[];
};

type EmptyDataStatisticPropsType = {
  title: string;
};

const CostTypesList = ({ costTypes }: CostTypesListProps) => {
  const typeOtherColor = '#00000073';
  const maxQuantityViewedCostBuckets = 4;
  const quantityOfUngroupedCostBuckets = 3;

  const handledCostTypes =
    costTypes && costTypes.length > maxQuantityViewedCostBuckets
      ? [
          ...costTypes.slice(0, quantityOfUngroupedCostBuckets),
          {
            name: 'Other types',
            color: typeOtherColor,
            totalCost: costTypes
              .slice(quantityOfUngroupedCostBuckets)
              .reduce((acc, { totalCost }) => acc + totalCost, 0),
          },
        ]
      : costTypes;

  const formattedCostTypes = handledCostTypes?.map((costType) => ({
    ...costType,
    totalCost: displayCurrency(costType.totalCost),
  }));

  return (
    <>
      {formattedCostTypes?.length
        ? formattedCostTypes.map(({ name, totalCost, color }) => (
            <div className={classes.costTypeItem} key={name}>
              <div className={classes.costTypeItemColor} style={{ backgroundColor: color }} />
              <Typography.Text>{name}</Typography.Text>
              <Typography.Text strong>$ {totalCost}</Typography.Text>
            </div>
          ))
        : null}
    </>
  );
};

const EmptyDataStatistic = ({ title }: EmptyDataStatisticPropsType) => {
  return (
    <Col className={classes.emptyDataCard}>
      <Typography.Text type="secondary">{title}</Typography.Text>
      <Typography.Text type="secondary">N/A</Typography.Text>
    </Col>
  );
};

export const ReportsStatistic = ({
  startDate,
  endDate,
  projectData,
  costTypesLegends,
}: ReportsStatisticPropsType) => {
  const { id } = useParams();
  const isRequestSkipped = !startDate || !endDate;
  const dateOfLastGeneratedReport = getMonthOfLastGeneratedReport(projectData.reports);
  const { data: summaryMetricsData } = useGetSummaryMetricsQuery(
    { id: id!, startDate: startDate, endDate: endDate },
    { skip: isRequestSkipped },
  );

  const costTypesTotalsGroupedByName =
    summaryMetricsData &&
    Object.entries(groupBy(summaryMetricsData.costTypes, ({ costType }) => costType))
      .map(([name, costs]) => ({
        name,
        totalCost: costs.reduce((acc, total) => acc + total.totalCost, 0),
        color: defaultChartColors[costTypesLegends.indexOf(name)],
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

  return (
    <>
      {summaryMetricsData?.totalHours ? (
        <div className={classes.statisticCardsWrapper}>
          <Col className={classes.statisticCard}>
            <Statistic
              title="Total Budget spent"
              value={summaryMetricsData.totalCost}
              groupSeparator=" "
              prefix="$"
              precision={2}
            />
          </Col>
          <Col className={classes.statisticCard}>
            <Statistic
              title="Total Workload hrs spent"
              value={summaryMetricsData.totalHours}
              groupSeparator=" "
              suffix="h"
              precision={2}
            />
          </Col>
          <Col className={classes.statisticCard}>
            <Statistic
              title="Average budget per month"
              value={summaryMetricsData.averageMonthCost}
              groupSeparator=" "
              precision={2}
              prefix="≈$"
            />
          </Col>
          <Col className={classes.statisticCard}>
            <div>
              <Typography.Text type="secondary">Cost bucket</Typography.Text>
            </div>
            <CostTypesList costTypes={costTypesTotalsGroupedByName} />
          </Col>
          <Col className={classes.statisticCard}>
            <Statistic
              title={`Total employees (as of report dated ${dateOfLastGeneratedReport})`}
              value={summaryMetricsData.totalEmployees}
            />
          </Col>
        </div>
      ) : (
        <div className={classes.statisticCardsWrapper}>
          <EmptyDataStatistic title="Total budget" />
          <EmptyDataStatistic title="Total workload hours" />
          <EmptyDataStatistic title="Average budget per month" />
          <EmptyDataStatistic title="Cost bucket" />
          <EmptyDataStatistic title="Total employees" />
        </div>
      )}
    </>
  );
};
